import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import Modal from "@/components/base/Modal";
import { ghostButtonClass, inputClass } from "@/components/base/fieldStyles";
import { formatNumber } from "@/utils/format";
import { buildDeleteModules } from "@/pages/admin/respaldo/components/selectiveDeleteModules";

const SelectiveDeletePanel = () => {
  const { data, deleteMany, clearAll } = useApp();
  const modules = useMemo(() => buildDeleteModules(data), [data]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [globalText, setGlobalText] = useState("");
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  const allSelected = modules.length > 0 && modules.every((m) => selected.has(m.key));
  const selectedModules = modules.filter((m) => selected.has(m.key));
  const selectedCount = selectedModules.reduce((acc, m) => acc + m.items.length, 0);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(modules.map((m) => m.key)));
  };

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 3600);
  };

  const runSelected = async () => {
    if (selectedModules.length === 0) return;
    setWorking(true);
    setError("");
    let total = 0;
    for (const m of selectedModules) {
      const ids = m.items.map((i) => i.id);
      if (ids.length > 0) total += await deleteMany(m.key, ids);
    }
    setWorking(false);
    setConfirmOpen(false);
    setSelected(new Set());
    notify(`${formatNumber(total)} registro(s) eliminado(s) de ${selectedModules.length} categoría(s).`);
  };

  const runGlobal = async () => {
    if (globalText.trim().toUpperCase() !== "VACIAR") return;
    setWorking(true);
    setError("");
    try {
      await clearAll();
      setGlobalOpen(false);
      setGlobalText("");
      setSelected(new Set());
      notify("Todos los datos operativos fueron borrados. Los usuarios se conservan.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo borrar todo.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-9 h-9 rounded-md bg-accent-500/25 text-accent-200 flex items-center justify-center shrink-0">
          <i className="ri-filter-off-line text-lg" />
        </span>
        <div>
          <h3 className="editable-title text-base font-bold">Borrado por categoría</h3>
          <p className="text-[11px] text-foreground-500 mt-0.5">
            Marca una o varias categorías y se borra <strong>todo lo de adentro</strong> de una sola
            vez. Los usuarios siempre se conservan.
          </p>
        </div>
      </div>

      <div className="rounded-md bg-secondary-500/15 border border-secondary-500/25 px-3 py-2 mb-4 flex items-start gap-2">
        <i className="ri-information-line text-secondary-200 mt-0.5" />
        <p className="text-[11px] text-foreground-700">
          Es un borrado <strong className="text-foreground-900">puntual</strong> por categoría. Si
          necesitas borrar absolutamente todo, usa el botón rojo del final.
        </p>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold text-foreground-600">Categorías</p>
        <button
          onClick={toggleAll}
          className="text-[11px] font-semibold text-accent-200 hover:text-accent-100 cursor-pointer whitespace-nowrap"
        >
          {allSelected ? "Quitar todo" : "Seleccionar todo"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {modules.map((m) => {
          const checked = selected.has(m.key);
          return (
            <label
              key={m.key}
              className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
                checked
                  ? "border-primary-400/60 bg-primary-500/10"
                  : "border-background-300/50 hover:border-accent-500/35"
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(m.key)}
                className="w-4 h-4 accent-primary-500 cursor-pointer shrink-0"
              />
              <span className="w-8 h-8 rounded-md bg-background-200/60 text-accent-200 flex items-center justify-center shrink-0">
                <i className={m.icon} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-foreground-900 truncate">
                  {m.label}
                </span>
                <span className="block text-[10px] text-foreground-500">
                  {formatNumber(m.items.length)} registro(s)
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {selectedModules.length > 0 && (
        <div className="mt-3 rounded-md glass-panel-strong border-accent-500/30 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 animate-fade-up">
          <p className="text-xs text-foreground-800 flex-1">
            <strong className="text-accent-200">{selectedModules.length}</strong> categoría(s) ·{" "}
            <strong className="text-accent-200">{formatNumber(selectedCount)}</strong> registro(s) que
            se borrarán.
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} className={ghostButtonClass}>
              Limpiar
            </button>
            <button
              onClick={() => setConfirmOpen(true)}
              className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-delete-bin-6-line" /> Vaciar seleccionadas
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-background-300/40 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-xs font-semibold text-foreground-900 flex items-center gap-1.5">
            <i className="ri-alarm-warning-line text-primary-200" /> Borrado total
          </p>
          <p className="text-[11px] text-foreground-500 mt-0.5">
            Elimina todas las categorías de una sola vez. Los usuarios se conservan.
          </p>
        </div>
        <button onClick={() => setGlobalOpen(true)} className={ghostButtonClass}>
          <i className="ri-delete-bin-7-line" /> Borrar todos los datos
        </button>
      </div>

      {feedback && (
        <p className="text-[11px] text-accent-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-checkbox-circle-line" /> {feedback}
        </p>
      )}
      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Vaciar categorías seleccionadas"
        icon="ri-alert-line"
        size="sm"
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button
              onClick={runSelected}
              disabled={working}
              className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-delete-bin-line" />{" "}
              {working ? "Borrando..." : "Sí, borrar"}
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          Vas a borrar <strong className="text-foreground-950">{formatNumber(selectedCount)}</strong>{" "}
          registro(s) de <strong className="text-foreground-950">{selectedModules.length}</strong>{" "}
          categoría(s):
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedModules.map((m) => (
            <span
              key={m.key}
              className="text-[10px] px-2 py-0.5 rounded-full bg-background-200/60 text-foreground-600 whitespace-nowrap"
            >
              {m.label}: {formatNumber(m.items.length)}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-foreground-500 mt-3">Esta acción no se puede deshacer.</p>
      </Modal>

      <Modal
        open={globalOpen}
        onClose={() => {
          setGlobalOpen(false);
          setGlobalText("");
        }}
        title="Borrar todos los datos"
        icon="ri-alarm-warning-line"
        size="sm"
        footer={
          <>
            <button
              onClick={() => {
                setGlobalOpen(false);
                setGlobalText("");
              }}
              className={ghostButtonClass}
            >
              Cancelar
            </button>
            <button
              onClick={runGlobal}
              disabled={working || globalText.trim().toUpperCase() !== "VACIAR"}
              className={`px-4 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap inline-flex items-center justify-center gap-2 ${
                globalText.trim().toUpperCase() === "VACIAR"
                  ? "bg-primary-500 hover:bg-primary-600 text-foreground-950 cursor-pointer"
                  : "bg-background-300/40 text-foreground-500 cursor-not-allowed"
              }`}
            >
              <i className="ri-delete-bin-7-line" /> {working ? "Borrando..." : "Borrar todo"}
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          Se eliminarán <strong className="text-foreground-950">todas las categorías</strong>:
          catálogo, clientes, ventas, inventario, gastos, cartera, préstamos, promociones, encargos,
          movimientos, reposiciones y puntos. Los usuarios se conservan.
        </p>
        <p className="text-sm text-foreground-700 mt-3">
          Escribe <strong className="text-foreground-950">VACIAR</strong> para confirmar:
        </p>
        <input
          value={globalText}
          onChange={(e) => setGlobalText(e.target.value)}
          placeholder="VACIAR"
          className={`${inputClass} mt-2`}
          aria-label="Confirmación de borrado total"
        />
      </Modal>
    </div>
  );
};

export default SelectiveDeletePanel;